import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

/**
 * LeaveDecisionModal
 * Props:
 * - isOpen: boolean – modal visibility
 * - onClose: func – close modal
 * - onSubmit: func – submit decision with comment
 * - leaveRequest: object – selected leave request details
 * - token: string – auth token for API calls
 * - userRole: string – role of the logged-in user ('hr_officer' or 'program_director')
 */
export default function LeaveDecisionModal({ isOpen, onClose, onSubmit, leaveRequest, token, userRole }) {
  const [comments, setComments] = useState("");
  const [policy, setPolicy] = useState(null);
  const [remainingDays, setRemainingDays] = useState(null);
  const [usedDays, setUsedDays] = useState(null);
  const [loading, setLoading] = useState(false);

  const selectedLeaveType = leaveRequest?.typeOfLeave?.toLowerCase().trim();
  const isHROfficer = userRole === 'hr_officer';
  const isProgramDirector = userRole === 'program_director';

  // HR Officer → fetch leave policy + usage
  useEffect(() => {
    if (isOpen && leaveRequest && isHROfficer) {
      setComments("");
      setPolicy(null);
      setRemainingDays(null);
      setUsedDays(null);

      if (selectedLeaveType && token && leaveRequest.staffId) {
        setLoading(true);

        (async () => {
          try {
            const policyRes = await axios.get(
              "https://ims.holdthechild.net/api/staff/leave-policy",
              { headers: { Authorization: `Bearer ${token}` } }
            );
            const policies = policyRes.data || [];

            const matched = policies.find(
              (p) => p.name.toLowerCase().trim() === selectedLeaveType
            );
            if (!matched) {
              setPolicy(null);
              setRemainingDays(null);
              setUsedDays(null);
              setLoading(false);
              return;
            }

            setPolicy(matched);

            const usageRes = await axios.get(
              `"https://ims.holdthechild.net"/api/staff/${leaveRequest.staffId}/leave-usage?type=${matched.name}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );

            const used = usageRes.data.used || 0;
            setUsedDays(used);

            if (["unlimited", "discretion"].includes(matched.type.toLowerCase())) {
              setRemainingDays(null);
            } else {
              setRemainingDays(matched.days - used);
            }
          } catch (error) {
            console.error("Error fetching leave policy or usage:", error);
            setPolicy(null);
            setRemainingDays(null);
            setUsedDays(null);
          } finally {
            setLoading(false);
          }
        })();
      }
    }
  }, [isOpen, leaveRequest, selectedLeaveType, token, isHROfficer]);

  // Program Director → start with empty comments
  useEffect(() => {
    if (isOpen && isProgramDirector) {
      setComments("");
    }
  }, [isOpen, isProgramDirector]);

  // Build payload for submission
  const handleSubmit = (decision) => {
    if (!decision) return;
    onSubmit({
      status: decision,
      comment: comments,
      role: userRole, // ✅ role-specific approval entry
    });
  };

  if (!isOpen || !leaveRequest) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center px-4">
      <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <h1 className="text-2xl font-bold text-orange-500 mb-6">Leave Request Review</h1>

        {/* Employee Details */}
        <h2 className="text-xl font-bold mb-4 text-center">Employee Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input label="First Name" value={leaveRequest.firstName} />
          <Input label="Last Name" value={leaveRequest.lastName} />
          <Input label="Department" value={leaveRequest.department} />
          <Input label="Role" value={leaveRequest.position} />
          <Input label="Replacement Person" value={leaveRequest.replacementPerson} />
          <Input label="Leave Type" value={leaveRequest.typeOfLeave} />
          <Input label="Start Date" value={new Date(leaveRequest.startDate).toLocaleDateString()} />
          <Input label="End Date" value={new Date(leaveRequest.endDate).toLocaleDateString()} />
        </div>

        {/* Leave Reason */}
        <div className="mb-6">
          <label className="block font-medium">Reason</label>
          <textarea
            value={leaveRequest.reason || ""}
            disabled
            className="w-full p-2 border rounded bg-gray-100"
            rows={2}
          />
        </div>

        {/* Eligibility Check (HR Only)
        {isHROfficer && (
          <div className="mb-6 border rounded p-4 bg-orange-50">
            <h3 className="font-semibold mb-2">Eligibility Check</h3>
            {loading ? (
              <p><FontAwesomeIcon icon={faSpinner} spin /> Loading policy details...</p>
            ) : !selectedLeaveType ? (
              <p className="italic text-gray-500">No leave type selected.</p>
            ) : policy === null ? (
              <p className="italic text-gray-500">Policy details not available.</p>
            ) : (
              <>
                <p><strong>Policy Type:</strong> {policy.type}</p>
                {remainingDays === null ? (
                  <p>This leave type has unlimited or discretionary days.</p>
                ) : (
                  <>
                    <p><strong>Days Allowed:</strong> {policy.days}</p>
                    <p><strong>Days Used:</strong> {usedDays}</p>
                    <p><strong>Remaining Days:</strong> {remainingDays < 0 ? 0 : remainingDays}</p>
                    {remainingDays < 0 && (
                      <p className="text-red-600 font-semibold">
                        Warning: Employee has exceeded allowed leave days!
                      </p>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )} */}

        {/* Comment Section */}
        <div className="mb-6">
          {isHROfficer && (
            <>
              <label className="block font-medium">Your Comment</label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="w-full p-2 border rounded"
                rows={3}
                placeholder="Write your recommendation comments here..."
              />
            </>
          )}

          {isProgramDirector && (
            <>
              <div className="mb-4">
                <label className="block font-semibold text-gray-700">
                  HR Recommendation & Comment:
                </label>
                <textarea
                  value={leaveRequest.approvals.find(a => a.role === 'hr_officer')?.comment || 'No comment provided'}
                  disabled
                  className="w-full p-2 border rounded bg-gray-100"
                  rows={2}
                />
              </div>
              <label className="block font-medium">Your Comment</label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="w-full p-2 border rounded"
                rows={3}
                placeholder="Write your decision comments here..."
              />
            </>
          )}
        </div>

        {/* Decision Buttons */}
        <div className="flex justify-center gap-4 mt-4">
          {isHROfficer && (
            <>
              <ActionButton text="Recommend Approve" decision="Approved" onClick={handleSubmit} color="orange" />
              <ActionButton text="Reject" decision="Rejected" onClick={handleSubmit} color="red" />
            </>
          )}

          {isProgramDirector && (
            <>
              <ActionButton text="Final Approve" decision="Approved" onClick={handleSubmit} color="orange" />
              <ActionButton text="Final Reject" decision="Rejected" onClick={handleSubmit} color="red" />
            </>
          )}
        </div>

        {/* Back Button */}
        <div className="mt-6 flex justify-center">
          <button
            onClick={onClose}
            className="border border-gray-400 px-4 py-2 rounded text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// Reusable input (read-only)
function Input({ label, value }) {
  return (
    <div>
      <label className="block font-medium">{label}</label>
      <input
        value={value || ""}
        disabled
        className="w-full p-2 border rounded bg-gray-100"
      />
    </div>
  );
}

// Reusable action button
function ActionButton({ text, decision, onClick, color }) {
  const colorClasses =
    color === "orange"
      ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
      : "bg-orange-100 text-red-600 border-orange-300 hover:bg-orange-200";

  return (
    <button
      onClick={() => onClick(decision)}
      className={`${colorClasses} px-6 py-2 rounded-lg border transition font-semibold`}
    >
      {text}
    </button>
  );
}
